/**
 * CNC PM PWA - LINE Anomaly Alert Dispatcher & Defect Trigger Engine
 * Detects NG/out-of-tolerance states, formats structured alert payloads,
 * dispatches via webhookClient, and enqueues to syncQueue on failure/offline.
 */

import type {
  InspectionRecord,
  ItemResult,
  LineAnomalyPayload,
  SyncQueueEntry,
} from '../../types';
import {
  db,
  getSetting,
  saveSetting,
  addToSyncQueue,
} from '../../db';
import {
  sendWebhookAlert,
  sendTestNotification as clientSendTestNotification,
  type AlertDispatchMode,
  type WebhookClientConfig,
} from './webhookClient';

export interface LineAlertConfig {
  enabled: boolean;
  mode: AlertDispatchMode;
  webhookUrl: string;
  apiToken: string;
  targetId: string;
  gasRelayUrl: string;
  updatedAt?: string;
}

export const DEFAULT_LINE_CONFIG: LineAlertConfig = {
  enabled: true,
  mode: 'direct_webhook',
  webhookUrl: '',
  apiToken: '',
  targetId: '',
  gasRelayUrl: '',
};

export const ABNORMAL_OPTION_STRINGS = [
  'ชำรุด',
  'รั่วซึม',
  'อุดตัน',
  'แห้งผิดปกติ',
  'สกปรก',
  'ผิดปกติ',
  'ขาด',
  'บุบ',
  'หลวม',
];

/**
 * Checks if a single ItemResult exhibits an NG or abnormal tolerance condition
 */
export function isItemDefective(item: ItemResult): boolean {
  if (item.status === 'NG' || item.isAbnormal) {
    return true;
  }

  // Check selected option against abnormal keywords
  if (item.selectedOption) {
    const opt = item.selectedOption.trim();
    if (ABNORMAL_OPTION_STRINGS.some((abn) => opt.includes(abn))) {
      return true;
    }
  }

  // Check numeric gauge value against standard thresholds if defined
  if (typeof item.numericValue === 'number') {
    // Known TOS WHN 13/15 Daily hydraulic threshold: >= 6.8 MPa
    if (item.itemNo === 4 && item.frequency === 'DAILY' && item.numericValue < 6.8) {
      return true;
    }
  }

  return false;
}

/**
 * Evaluates whether an inspection record contains any defects or out-of-tolerance readings
 */
export function hasInspectionDefects(record: InspectionRecord): boolean {
  if (record.overallStatus === 'DEFECT_FOUND') {
    return true;
  }
  if (!record.results || !Array.isArray(record.results)) {
    return false;
  }
  return record.results.some(isItemDefective);
}

/**
 * Extracts structured defect objects from an inspection record
 */
export function extractDefectsFromInspection(record: InspectionRecord): LineAnomalyPayload['defects'] {
  if (!record.results || !Array.isArray(record.results)) {
    return [];
  }

  const defectiveItems = record.results.filter(isItemDefective);

  return defectiveItems.map((item) => {
    let measuredVal: string | number = 'NG';

    if (typeof item.numericValue === 'number') {
      measuredVal = `${item.numericValue} MPa`;
    } else if (item.measuredValue !== undefined && item.measuredValue !== null && item.measuredValue !== '') {
      measuredVal = item.measuredValue;
    } else if (item.selectedOption) {
      measuredVal = item.selectedOption;
    }

    let notesText = item.notes ? item.notes.trim() : '';
    if (!notesText && typeof item.numericValue === 'number' && item.itemNo === 4 && item.numericValue < 6.8) {
      notesText = `แรงดันไฮดรอลิกตกต่ำกว่าเกณฑ์มาตรฐาน 6.8 MPa (วัดได้ ${item.numericValue} MPa)`;
    }
    if (!notesText) {
      notesText = `ตรวจพบข้อบกพร่อง / ค่าผิดปกติในการตรวจสอบข้อ ${item.itemNo}`;
    }

    return {
      itemNo: item.itemNo,
      task: item.taskName || `รายการตรวจสอบที่ ${item.itemNo}`,
      criteria: item.criteria || 'เกณฑ์มาตรฐานความปลอดภัย',
      measuredValue: measuredVal,
      status: 'NG',
      notes: notesText,
    };
  });
}

/**
 * Formats a complete LineAnomalyPayload compliant with contracts.mjs
 */
export function buildLineAnomalyPayload(
  record: InspectionRecord,
  appBaseUrl?: string
): LineAnomalyPayload {
  const defects = extractDefectsFromInspection(record);
  const origin =
    appBaseUrl ||
    (typeof window !== 'undefined' ? window.location.origin : 'https://pwa.factory.local');
  const recordUrl = `${origin}/records/${record.id}`;

  return {
    machineId: record.machineId,
    machineName: record.machineName,
    inspectorName: record.inspectorName,
    inspectionDate: record.inspectionDate,
    operatingHours: record.operatingHours,
    overallStatus: 'DEFECT_FOUND',
    defects: defects.length > 0 ? defects : [
      {
        itemNo: 1,
        task: 'ข้อบกพร่องทั่วไป (General Anomaly)',
        criteria: 'เกณฑ์การบำรุงรักษา',
        measuredValue: 'NG',
        status: 'NG',
        notes: record.supervisorNotes || 'พบความผิดปกติในการตรวจสอบเครื่องจักร',
      },
    ],
    recordUrl,
  };
}

/**
 * Loads the active LINE Alert configuration from Dexie settings store
 */
export async function getLineAlertConfig(): Promise<LineAlertConfig> {
  const saved = await getSetting<Partial<LineAlertConfig>>('line_config');
  const fallbackUrl = (await getSetting<string>('line_webhook_url')) || '';
  const fallbackGasUrl = (await getSetting<string>('gas_endpoint_url')) || '';

  return {
    ...DEFAULT_LINE_CONFIG,
    ...saved,
    webhookUrl: saved?.webhookUrl || fallbackUrl,
    gasRelayUrl: saved?.gasRelayUrl || fallbackGasUrl,
  };
}

/**
 * Saves updated LINE Alert configuration to Dexie settings store
 */
export async function saveLineAlertConfig(config: Partial<LineAlertConfig>): Promise<void> {
  const current = await getLineAlertConfig();
  const updated: LineAlertConfig = {
    ...current,
    ...config,
  };

  await saveSetting('line_config', updated);
  if (updated.webhookUrl) {
    await saveSetting('line_webhook_url', updated.webhookUrl);
  }
}

/**
 * Dispatches an anomaly alert for a completed inspection record.
 * If offline or HTTP dispatch fails, securely enqueues payload to syncQueue.
 */
export async function dispatchAnomalyAlert(
  record: InspectionRecord,
  options?: {
    force?: boolean;
    config?: Partial<LineAlertConfig>;
    appBaseUrl?: string;
  }
): Promise<{ dispatched: boolean; queued: boolean; error?: string }> {
  // 1. Check if record contains any defects
  if (!options?.force && !hasInspectionDefects(record)) {
    return { dispatched: false, queued: false };
  }

  // 2. Load active configuration
  const baseConfig = await getLineAlertConfig();
  const config: LineAlertConfig = {
    ...baseConfig,
    ...options?.config,
  };

  if (!config.enabled && !options?.force) {
    console.log('[LineAlert] Alerts are currently disabled in settings.');
    return { dispatched: false, queued: false };
  }

  // 3. Assemble LineAnomalyPayload
  const payload = buildLineAnomalyPayload(record, options?.appBaseUrl);

  const clientConfig: WebhookClientConfig = {
    mode: config.mode,
    webhookUrl: config.webhookUrl,
    apiToken: config.apiToken,
    targetId: config.targetId,
    gasRelayUrl: config.gasRelayUrl,
  };

  // Determine target endpoint URL for queue logging
  const effectiveTargetUrl =
    config.mode === 'gas_relay'
      ? config.gasRelayUrl || config.webhookUrl
      : config.webhookUrl || 'https://notify-api.line.me/api/notify';

  // 4. Check if offline
  const isBrowserOffline =
    typeof navigator !== 'undefined' &&
    typeof navigator.onLine === 'boolean' &&
    !navigator.onLine;

  if (isBrowserOffline || (!config.webhookUrl && !config.apiToken && !config.gasRelayUrl)) {
    // Queue offline alert for subsequent sync
    await addToSyncQueue({
      queueId: `line_alert_${record.id}_${Date.now()}`,
      recordId: record.id,
      endpointType: 'line_webhook',
      targetUrl: effectiveTargetUrl,
      payload,
      attempts: 0,
      maxAttempts: 5,
      status: 'PENDING',
      createdAt: new Date().toISOString(),
    });

    console.log('[LineAlert] Device offline or endpoint not yet configured; queued to syncQueue.');
    return { dispatched: false, queued: true };
  }

  // 5. Attempt immediate transmission
  try {
    const result = await sendWebhookAlert(payload, clientConfig);

    if (result.success) {
      // Mark alertDispatched on inspection record
      await db.inspections.update(record.id, {
        alertDispatched: true,
        updatedAt: new Date().toISOString(),
      });
      return { dispatched: true, queued: false };
    } else {
      throw new Error(result.error || `HTTP dispatch failed with status ${result.status}`);
    }
  } catch (dispatchErr: any) {
    console.warn('[LineAlert] Immediate dispatch failed, enqueuing to syncQueue:', dispatchErr);

    // Enqueue to syncQueue so retry engine handles it
    await addToSyncQueue({
      queueId: `line_alert_${record.id}_${Date.now()}`,
      recordId: record.id,
      endpointType: 'line_webhook',
      targetUrl: effectiveTargetUrl,
      payload,
      attempts: 1,
      maxAttempts: 5,
      status: 'PENDING',
      lastError: dispatchErr.message || 'Dispatch error',
      createdAt: new Date().toISOString(),
    });

    return {
      dispatched: false,
      queued: true,
      error: dispatchErr.message || 'Alert failed and was queued',
    };
  }
}

/**
 * High-level test notification dispatcher for settings panel
 */
export async function sendTestAlert(
  customConfig?: Partial<LineAlertConfig>
): Promise<{ success: boolean; message: string; timestamp: string }> {
  const currentConfig = await getLineAlertConfig();
  const mergedConfig: LineAlertConfig = {
    ...currentConfig,
    ...customConfig,
  };

  return await clientSendTestNotification({
    mode: mergedConfig.mode,
    webhookUrl: mergedConfig.webhookUrl,
    apiToken: mergedConfig.apiToken,
    targetId: mergedConfig.targetId,
    gasRelayUrl: mergedConfig.gasRelayUrl,
  });
}
